import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Tiny localStorage helpers — silent on failure (private-mode, quota, etc.)
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("mukti_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistUser = (user) => {
  try {
    if (user) localStorage.setItem("mukti_user", JSON.stringify(user));
    else localStorage.removeItem("mukti_user");
  } catch {
    /* private mode / quota — display copy isn't critical */
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(false);
  // While a login is in-flight, suppress the mount-time /auth/me reconciliation
  // so its 401 catch cannot wipe the user we just authenticated.
  const isLoggingIn = useRef(false);

  // Reconcile local user with server session on mount — but only if there's
  // already a stored user. Anonymous visitors to /splash or /login do not
  // need to probe /auth/me (which would 401 and pollute the console).
  useEffect(() => {
    if (!readStoredUser()) return undefined;
    let active = true;
    api
      .get("/auth/me")
      .then((res) => {
        if (!active || isLoggingIn.current) return;
        setUser(res.data);
        persistUser(res.data);
      })
      .catch(() => {
        // 401 already handled by axios interceptor; just clear local copy.
        if (!active || isLoggingIn.current) return;
        persistUser(null);
        setUser(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    isLoggingIn.current = true;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persistUser(data.user);
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
      // Release the lock on the next tick so any in-flight /auth/me catch
      // (already queued in the microtask queue) still sees the lock set.
      setTimeout(() => {
        isLoggingIn.current = false;
      }, 0);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* Already-expired session or network glitch — still clear locally. */
    }
    persistUser(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, loading }),
    [user, login, logout, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
